/* ============================================
   TranscripcionAudio — Nota de voz pasada a texto
   A PEDIDO: en reposo es solo un ícono. Se transcribe
   cuando alguien lo pide, no cuando el audio llega —
   la mayoría de los audios se escuchan y se olvidan,
   y transcribirlos todos sería pagarle al modelo por
   un texto que nadie va a leer.
   ============================================ */
import { useState } from 'react'
import { IconCopy, IconCheck, IconRefresh, IconFileText } from '@/components/icons/Icons'
import type { TranscripcionStatus } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** Más que esto se recorta y se muestra con "Ver más": una nota larga tapa el chat. */
const CORTE_CARACTERES = 220

interface Props {
  mensajeId: number
  texto: string | null
  status: TranscripcionStatus | null
  /**
   * Sin el botón de pedir. Transcribir cuesta una llamada a Gemini de NUESTRA
   * cuenta, así que al partner se le muestra el texto si ya existe —ese ya se
   * pagó— pero no la herramienta para pedir uno nuevo. Mientras nadie decida
   * quién paga su consumo de IA, el botón es nuestro.
   */
  soloLectura?: boolean
}

export default function TranscripcionAudio({ mensajeId, texto, status, soloLectura = false }: Props) {
  const [expandida, setExpandida] = useState(false)
  const [copiado, setCopiado] = useState(false)
  const [pidiendo, setPidiendo] = useState(false)
  const [errorPedido, setErrorPedido] = useState(false)

  const copiar = async () => {
    if (!texto) return
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 1600)
    } catch {
      /* el navegador negó el portapapeles: el texto está a la vista para seleccionarlo */
    }
  }

  const pedirTranscripcion = async () => {
    setPidiendo(true)
    setErrorPedido(false)
    try {
      const token = localStorage.getItem('auth_token')
      const res = await fetch(`${API_URL}/api/chat/mensajes/${mensajeId}/transcribir`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(String(res.status))
      // El resultado llega solo por SSE (`message_updated`) y repinta la burbuja.
    } catch {
      setErrorPedido(true)
      setPidiendo(false)
    }
  }

  if (status === 'pendiente' || pidiendo) {
    return (
      <div className="chat-transcripcion chat-transcripcion--pedir">
        <span className="chat-transcripcion__estado">Transcribiendo…</span>
      </div>
    )
  }

  if (!texto) {
    // Sin texto y sin permiso para pedirlo no hay nada que mostrar: un botón
    // que no se puede pulsar ocupa el mismo lugar que uno que sí.
    if (soloLectura) return null

    // Nunca se intentó (lo normal): solo el ícono, sin texto ni separador. Es una
    // herramienta disponible, no un aviso de que falta algo.
    const yaFallo = errorPedido || status === 'fallida' || status === 'omitida'

    if (!yaFallo) {
      return (
        <div className="chat-transcripcion chat-transcripcion--pedir">
          <button
            type="button"
            className="chat-transcripcion__pedir"
            onClick={pedirTranscripcion}
            title="Transcribir a texto"
            aria-label="Transcribir a texto"
          >
            <IconFileText size={14} />
          </button>
        </div>
      )
    }

    const motivo = errorPedido
      ? 'No se pudo pedir la transcripción'
      : status === 'omitida'
        ? 'Sin texto para transcribir'
        : 'No se pudo transcribir'

    return (
      <div className="chat-transcripcion chat-transcripcion--pedir">
        <span className="chat-transcripcion__estado">{motivo}</span>
        <button
          type="button"
          className="chat-transcripcion__accion"
          onClick={pedirTranscripcion}
          title="Reintentar la transcripción"
        >
          <IconRefresh size={13} />
          <span>Reintentar</span>
        </button>
      </div>
    )
  }

  const largo = texto.length > CORTE_CARACTERES
  const visible = largo && !expandida ? `${texto.slice(0, CORTE_CARACTERES).trimEnd()}…` : texto

  return (
    <div className="chat-transcripcion">
      <p className="chat-transcripcion__texto">{visible}</p>

      <div className="chat-transcripcion__pie">
        {largo && (
          <button
            type="button"
            className="chat-transcripcion__accion"
            onClick={() => setExpandida(v => !v)}
          >
            {expandida ? 'Ver menos' : 'Ver más'}
          </button>
        )}

        <button
          type="button"
          className="chat-transcripcion__accion"
          onClick={copiar}
          title="Copiar transcripción"
        >
          {copiado ? <IconCheck size={13} /> : <IconCopy size={13} />}
          <span>{copiado ? 'Copiado' : 'Copiar'}</span>
        </button>
      </div>
    </div>
  )
}
